import axios from "axios";
import { apiConfig, loginRequest } from "../authConfig";
import { msalInstance } from "../AuthProvider";

const api = axios.create({
    baseURL: apiConfig.baseUrl,
    headers: {
        "Content-Type": "application/json",
    },
});

// Acquire a fresh token on every request rather than reusing a single ID
// token cached once at login. acquireTokenSilent returns the still-valid
// cached token when possible, or silently renews it (via the hidden
// refresh-token flow) when it's expired or close to expiring -- so this is
// the only thing that needs to run per-request; nothing else needs to
// think about token lifetime. Previously the app read a token that was
// fetched once in useUserProfile.ts and stashed in sessionStorage, which
// meant every request after that token's ~60-90 minute lifetime elapsed
// failed with "Invalid token: Signature has expired" on the backend, even
// though the user was still actively signed in.
//
// 2026-08-05: acquireTokenSilent can itself fail (session revoked, refresh
// token expired, MSAL cache cleared, clock skew, etc.). That used to just
// get logged to the console while the request went out with no
// Authorization header at all -- the backend correctly 401's it, but the
// user just sees whatever generic "Failed to ..." message the calling page
// happens to show, with no indication it's actually an auth problem (this
// is exactly what caused the confusing "Failed to generate" error on the
// Timesheet Validation page). Now: fail the request instead of silently
// sending it unauthenticated, and kick off a full-page redirect through
// Azure AD sign-in so the session actually gets fixed. Guarded so multiple
// in-flight requests failing at once only trigger one redirect.
let redirectInFlight = false;

api.interceptors.request.use(async (config) => {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!account) {
        return config;
    }
    try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
        });
        config.headers.Authorization = `Bearer ${tokenResponse.idToken}`;
        return config;
    } catch (err) {
        console.error("Failed to acquire a fresh token for API request -- redirecting to sign-in", err);
        if (!redirectInFlight) {
            redirectInFlight = true;
            msalInstance.acquireTokenRedirect({ ...loginRequest, account });
        }
        return Promise.reject(err);
    }
});

// 2026-08-05: acquireTokenSilent's cache-freshness check can, in rare cases,
// still hand back an ID token whose exp claim has already passed by the
// time it's decoded on the backend (its cache-hit logic is keyed off the
// access token's expiry, not necessarily the ID token's) -- seen live as
// "Invalid token: Signature has expired" on Approval Queue's Admin View
// even right after the request-interceptor fix above. Rather than try to
// reason about MSAL's internal cache logic, just detect that specific
// backend rejection and force a real (non-cached) token refresh, then
// retry the request once. A regular request-interceptor retry can't do
// this because it doesn't see the response -- this has to be a response
// interceptor.
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error?.config;
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const isExpiredTokenError =
            status === 401 && typeof detail === "string" && detail.startsWith("Invalid token");

        if (!isExpiredTokenError || !original || original._retriedAfterForceRefresh) {
            return Promise.reject(error);
        }

        const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
        if (!account) {
            return Promise.reject(error);
        }

        original._retriedAfterForceRefresh = true;
        try {
            const tokenResponse = await msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
                forceRefresh: true,
            });
            original.headers.Authorization = `Bearer ${tokenResponse.idToken}`;
            return api(original);
        } catch (refreshErr) {
            console.error("Forced token refresh after expired-token 401 also failed -- redirecting to sign-in", refreshErr);
            if (!redirectInFlight) {
                redirectInFlight = true;
                msalInstance.acquireTokenRedirect({ ...loginRequest, account });
            }
            return Promise.reject(refreshErr);
        }
    }
);

export interface UserProfile {
    id: number;
    email: string;
    display_name: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    is_subcontractor: boolean;
    roles: string[];
}

export interface Project {
    id: number;
    zoho_id: string;
    name: string;
    customer: string;
    billing_type: string;
    is_whitelisted: boolean;
    pm_name: string | null;
}

export interface Task {
    id: number;
    zoho_id: string;
    name: string;
    billing_rate: number;
}

export const getMe = async (): Promise<UserProfile> => {
    const response = await api.get("/auth/roles");
    return response.data;
};

export const getProjects = async (): Promise<Project[]> => {
    const response = await api.get("/sync/projects");
    return response.data.projects;
};

// Returns only the projects the current user may log time against (Zoho
// assignment, manual whitelist, or an internal Amplify/Fuse bucket open to
// everyone) -- used by the Timesheet Entry project dropdown. GET /sync/projects
// (above) returns every active project and is used by the Project Financials
// report selector, which needs the full list regardless of assignment.
export const getMyProjects = async (): Promise<Project[]> => {
    const response = await api.get("/sync/my-projects");
    return response.data.projects;
};

export const getProjectTasks = async (projectId: number): Promise<Task[]> => {
    const response = await api.get(`/sync/projects/${projectId}/tasks`);
    return response.data.tasks;
};

export interface TimeEntryPayload {
    project_id: number;
    task_id: number;
    entry_date: string;
    hours: number;
    comments: string;
}

export interface TimesheetPayload {
    period_start: string;
    period_end: string;
    is_partial: boolean;
    entries: TimeEntryPayload[];
}

export const saveDraft = async (payload: TimesheetPayload): Promise<any> => {
    const response = await api.post("/timesheets/draft", payload);
    return response.data;
};

export const getDraft = async (periodStart: string, periodEnd: string): Promise<any> => {
    const response = await api.get("/timesheets/draft", {
        params: { period_start: periodStart, period_end: periodEnd }
    });
    return response.data;
};

export const submitTimesheet = async (timesheetId: number): Promise<any> => {
    const response = await api.post(`/timesheets/${timesheetId}/submit`);
    return response.data;
};

export interface FinancialsReportRequest {
    mode: string;
    selected_ids?: number[];
    selected_names?: string[];
    billing_type?: string;
}

export const generateFinancialsReport = async (payload: any): Promise<Blob> => {
    const response = await api.post("/reports/financials", payload, {
        responseType: "blob"
    });
    return response.data;
};

export const exportTimesheetXls = async (timesheetId: number): Promise<Blob> => {
    const response = await api.get(`/timesheets/${timesheetId}/export-xls`, {
        responseType: "blob"
    });
    return response.data;
};

export interface AppUser {
    id: number;
    email: string;
    display_name: string;
    first_name: string;
    last_name: string;
    zoho_user_id: string | null;
    is_active: boolean;
    can_approve_expenses: boolean;
    is_subcontractor: boolean;
    roles: string[];
}

export interface Role {
    id: number;
    name: string;
    description: string | null;
}

export interface AzureUser {
    azure_ad_id: string;
    display_name: string;
    first_name: string;
    last_name: string;
    email: string;
}

export interface CreateUserPayload {
    azure_ad_id: string;
    email: string;
    first_name: string;
    last_name: string;
    display_name: string;
    zoho_user_id?: string | null;
    roles: string[];
    is_subcontractor?: boolean;
}

export interface UpdateUserPayload {
    roles?: string[];
    zoho_user_id?: string | null;
    is_active?: boolean;
    can_approve_expenses?: boolean;
    is_subcontractor?: boolean;
}

export interface SubcontractorPoolProject {
    id: number;
    name: string;
    customer: string | null;
}

export const getUsers = async (): Promise<AppUser[]> => {
    const response = await api.get("/users");
    return response.data;
};

export const getRoles = async (): Promise<Role[]> => {
    const response = await api.get("/users/roles");
    return response.data;
};

export const searchAzureUsers = async (q: string): Promise<AzureUser[]> => {
    const response = await api.get("/users/search-azure", { params: { q } });
    return response.data;
};

export const createUser = async (payload: CreateUserPayload): Promise<AppUser> => {
    const response = await api.post("/users", payload);
    return response.data;
};

export const updateUser = async (userId: number, payload: UpdateUserPayload): Promise<AppUser> => {
    const response = await api.put(`/users/${userId}`, payload);
    return response.data;
};

export const getSubcontractorPoolAccount = async (): Promise<{ zoho_user_id: string | null }> => {
    const response = await api.get("/users/subcontractor-pool-account");
    return response.data;
};

export const setSubcontractorPoolAccount = async (zohoUserId: string): Promise<{ zoho_user_id: string }> => {
    const response = await api.put("/users/subcontractor-pool-account", { zoho_user_id: zohoUserId });
    return response.data;
};

export const getSubcontractorPoolProjects = async (): Promise<SubcontractorPoolProject[]> => {
    const response = await api.get("/users/subcontractor-pool-projects");
    return response.data;
};

export const getUserSubcontractorProjects = async (userId: number): Promise<{ project_ids: number[] }> => {
    const response = await api.get(`/users/${userId}/subcontractor-projects`);
    return response.data;
};

export const setUserSubcontractorProjects = async (userId: number, projectIds: number[]): Promise<{ project_ids: number[] }> => {
    const response = await api.put(`/users/${userId}/subcontractor-projects`, { project_ids: projectIds });
    return response.data;
};

export const getApprovalQueue = async (mode: "pm" | "admin" = "pm"): Promise<any> => {
    const response = await api.get(`/approvals/queue?mode=${mode}`);
    return response.data;
};

export const getPendingRejections = async (): Promise<any> => {
    const response = await api.get(`/approvals/consultant/pending-rejections`);
    return response.data;
};

export const getApprovalDetail = async (projectId: number, mode: "pm" | "admin" = "pm"): Promise<any> => {
    const response = await api.get(`/approvals/project/${projectId}?mode=${mode}`);
    return response.data;
};

export const submitApprovalAction = async (projectId: number, payload: any, mode: "pm" | "admin" = "pm"): Promise<any> => {
    const response = await api.post(`/approvals/project/${projectId}/action?mode=${mode}`, payload);
    return response.data;
};

export interface ZohoSyncSummary {
    projects_created?: number;
    projects_updated?: number;
    projects_deactivated?: number;
    projects_checked?: number;
    tasks_synced?: number;
    assignments_synced?: number;
    synced_at: string;
}

export interface ZohoSyncResult {
    status: string;
    message: string;
    summary: ZohoSyncSummary;
}

export const triggerZohoSync = async (): Promise<ZohoSyncResult> => {
    const response = await api.post("/sync/zoho");
    return response.data;
};

export const triggerZohoProjectsSync = async (): Promise<ZohoSyncResult> => {
    const response = await api.post("/sync/zoho/projects");
    return response.data;
};

export const triggerZohoTasksSync = async (): Promise<ZohoSyncResult> => {
    const response = await api.post("/sync/zoho/tasks");
    return response.data;
};

export const triggerZohoAssignmentsSync = async (): Promise<ZohoSyncResult> => {
    const response = await api.post("/sync/zoho/assignments");
    return response.data;
};

// ---------------------------------------------------------------------------
// Commission Hours
// ---------------------------------------------------------------------------

export interface CommissionTier {
    threshold: number | null;
    rate: number;
}

export interface CommissionPlan {
    id: number;
    employee_name: string;
    zoho_user_name: string;
    user_id: number | null;
    tiers: CommissionTier[];
    customer_commission_pct: number | null;
    customer_commission_customer: string | null;
    is_active: boolean;
}

export interface CommissionLine {
    id: number;
    date: string;
    project: string;
    task: string;
    notes: string;
    hours: number;
    included: boolean;
}

export interface CommissionRun {
    id: number;
    plan_id: number;
    employee_name: string;
    year: number;
    month: number;
    status: "draft" | "finalized";
    included_hours: number;
    cumulative_hours_before: number;
    cumulative_hours_after: number;
    tier_payout: number;
    prior_year_december_netting: number;
    payout_override: number | null;
    computed_final_payout: number;
    final_payout: number;
    calculated_diff: number;
    customer_commission_amount: number | null;
    customer_commission_pct: number | null;
    customer_commission_customer: string | null;
    customer_commission_payroll_month: string | null;
    finalized_at: string | null;
    lines: CommissionLine[];
}

export interface CommissionRunsListItem {
    plan_id: number;
    employee_name: string;
    run: CommissionRun | null;
}

export const getCommissionPlans = async (): Promise<CommissionPlan[]> => {
    const response = await api.get("/commissions/plans");
    return response.data;
};

export const updateCommissionPlan = async (planId: number, payload: Partial<{
    tiers: CommissionTier[];
    customer_commission_pct: number;
    customer_commission_customer: string;
    is_active: boolean;
}>): Promise<CommissionPlan> => {
    const response = await api.put(`/commissions/plans/${planId}`, payload);
    return response.data;
};

export interface ExcludedProject {
    id: number;
    project_name: string;
}

export const getCommissionExcludedProjects = async (): Promise<ExcludedProject[]> => {
    const response = await api.get("/commissions/excluded-projects");
    return response.data;
};

export const addCommissionExcludedProject = async (projectName: string): Promise<ExcludedProject> => {
    const response = await api.post("/commissions/excluded-projects", { project_name: projectName });
    return response.data;
};

export const removeCommissionExcludedProject = async (id: number): Promise<void> => {
    await api.delete(`/commissions/excluded-projects/${id}`);
};

export const getCommissionRuns = async (year: number, month: number): Promise<{
    year: number; month: number; employees: CommissionRunsListItem[];
}> => {
    const response = await api.get("/commissions/runs", { params: { year, month } });
    return response.data;
};

export const generateCommissionRuns = async (year: number, month: number, regenerate = false): Promise<{
    employees: CommissionRun[]; excluded_hours_total: number;
}> => {
    const response = await api.post("/commissions/runs/generate", { year, month, regenerate });
    return response.data;
};

export const toggleCommissionLine = async (runId: number, lineId: number, included: boolean): Promise<CommissionRun> => {
    const response = await api.patch(`/commissions/runs/${runId}/lines/${lineId}`, { included });
    return response.data;
};

export const updateCommissionRun = async (runId: number, payload: Partial<{
    prior_year_december_netting: number;
    payout_override: number;
    clear_payout_override: boolean;
    customer_commission_amount: number;
    customer_commission_payroll_month: string;
}>): Promise<CommissionRun> => {
    const response = await api.patch(`/commissions/runs/${runId}`, payload);
    return response.data;
};

export const finalizeCommissionRun = async (runId: number): Promise<CommissionRun> => {
    const response = await api.post(`/commissions/runs/${runId}/finalize`);
    return response.data;
};

export const unfinalizeCommissionRun = async (runId: number): Promise<CommissionRun> => {
    const response = await api.post(`/commissions/runs/${runId}/unfinalize`);
    return response.data;
};

// View-only, self-scoped Commission Hours -- for any consultant-role user
// (never subcontractors) to see only their own linked plan/run. Distinct
// from getCommissionRuns above, which returns every employee's data and is
// restricted to hr_payroll/admin.
export const getMyCommissionRun = async (year: number, month: number): Promise<{
    year: number; month: number; has_plan: boolean; employee_name: string; run: CommissionRun | null;
}> => {
    const response = await api.get("/commissions/my-run", { params: { year, month } });
    return response.data;
};

// ---------------------------------------------------------------------------
// Project Info (Reports > Project Info)
// ---------------------------------------------------------------------------

export interface ProjectInfoSnapshot {
    project_id: number;
    project_name: string;
    customer_name: string | null;
    project_type: "T&M" | "Fixed";
    is_alerted: boolean;
    alert_comment: string | null;
    has_past_due_milestone: boolean;
    po_number: string | null;
    budget: number;
    // 2026-08-06: stored for future margin reporting -- not rendered
    // anywhere on Project Info yet.
    cost_budget: number | null;
    actuals: number | null;
    pending: number | null;
    remaining: number | null;
    etc: number | null;
    eac: number | null;
    vac: number | null;
    expense_budget: number | null;
    expenses_used: number | null;
    expenses_remaining: number | null;
    last_computed_at: string | null;
}

export interface ProjectMilestoneInfo {
    id: number;
    milestone_number: number;
    description: string | null;
    value: number;
    exp_inv_date: string | null;
    invoice_date: string | null;
    invoice_number: string | null;
    is_invoiced: boolean;
    is_past_due: boolean;
}

export interface TimesheetDetailRow {
    consultant: string;
    task: string;
    date: string | null;
    hours: number;
    rate: number;
    cost: number;
    comments: string;
    // 2026-08-26, backlog #10: billed_status/invoice_id come straight from
    // Zoho's time-entry payload; invoice_number is resolved server-side
    // from invoice_id (Zoho's internal ID) via a cached GET /invoices/{id}
    // lookup, since invoice_id itself isn't human-readable.
    billed_status: string | null;
    invoice_id: string | null;
    invoice_number: string | null;
}

export interface ProjectInfoDetail extends ProjectInfoSnapshot {
    milestones?: ProjectMilestoneInfo[];
    budgeted_labor_hours?: number | null;
    actual_labor_hours?: number;
    timesheet_details: TimesheetDetailRow[];
}

export const getProjectInfoList = async (viewMode: "all" | "mine" = "all"): Promise<{ projects: ProjectInfoSnapshot[] }> => {
    const response = await api.get(`/project-info?view=${viewMode}`);
    return response.data;
};

export const refreshProjectInfo = async (): Promise<{ status: string; warnings: Record<string, string[]> }> => {
    const response = await api.post("/project-info/refresh");
    return response.data;
};

export const getProjectInfoDetail = async (projectId: number): Promise<ProjectInfoDetail> => {
    const response = await api.get(`/project-info/${projectId}`);
    return response.data;
};

export const updateProjectInfoManualFields = async (
    projectId: number,
    payload: Partial<{ etc: number; budgeted_labor_hours: number; expense_budget: number; expenses_used: number }>,
): Promise<{ status: string }> => {
    const response = await api.patch(`/project-info/${projectId}/manual-fields`, payload);
    return response.data;
};

export interface ExcludableProject {
    project_id: number;
    project_name: string;
    is_excluded_from_view: boolean;
}

export const getExcludableProjects = async (): Promise<{ projects: ExcludableProject[] }> => {
    const response = await api.get("/project-info/admin/excludable");
    return response.data;
};

export const setProjectExcluded = async (projectId: number, excluded: boolean): Promise<{ status: string }> => {
    const response = await api.patch(`/project-info/${projectId}/exclude`, null, { params: { excluded } });
    return response.data;
};

export const setAlertedProjects = async (
    projects: { project_id: number; comment: string }[]
): Promise<{ status: string; alerted_count: number }> => {
    const response = await api.post("/project-info/admin/alerts", { projects });
    return response.data;
};

export interface ExcludableInvoicingProject {
    project_id: number;
    project_name: string;
    is_excluded_from_invoicing: boolean;
}

export const getExcludableInvoicingProjects = async (): Promise<{ projects: ExcludableInvoicingProject[] }> => {
    const response = await api.get("/project-info/admin/excludable-invoicing");
    return response.data;
};

export const setProjectExcludedFromInvoicing = async (projectId: number, excluded: boolean): Promise<{ status: string }> => {
    const response = await api.patch(`/project-info/${projectId}/exclude-invoicing`, null, { params: { excluded } });
    return response.data;
};

export const createProjectMilestone = async (
    projectId: number,
    payload: { description: string; value: number; exp_inv_date: string; invoice_date?: string | null; invoice_number?: string | null },
): Promise<ProjectMilestoneInfo> => {
    const response = await api.post(`/project-info/${projectId}/milestones`, payload);
    return response.data;
};

export const updateProjectMilestone = async (
    milestoneId: number,
    payload: Partial<{ description: string; value: number; exp_inv_date: string | null; invoice_date: string | null; invoice_number: string | null; clear_invoice: boolean }>,
): Promise<ProjectMilestoneInfo> => {
    const response = await api.patch(`/project-info/milestones/${milestoneId}`, payload);
    return response.data;
};

export const deleteProjectMilestone = async (milestoneId: number): Promise<{ status: string }> => {
    const response = await api.delete(`/project-info/milestones/${milestoneId}`);
    return response.data;
};

export const getPastDueMilestoneProjects = async (): Promise<{ projects: { project_id: number; project_name: string }[] }> => {
    const response = await api.get(`/project-info/past-due-milestones`);
    return response.data;
};

// ---------------------------------------------------------------------------
// Data Quality Report (Reports > Data Quality Report, admin-only, 2026-08-06)
// ---------------------------------------------------------------------------

export interface DataQualityIssue {
    project_id: number;
    project_name: string;
    message: string;
}

export interface DataQualityReportResponse {
    issues: {
        po_number: DataQualityIssue[];
        pm: DataQualityIssue[];
        budget: DataQualityIssue[];
        rate: DataQualityIssue[];
        zero_actuals: DataQualityIssue[];
        stale_sync: DataQualityIssue[];
        billing_type: DataQualityIssue[];
    };
}

export const getDataQualityReport = async (): Promise<DataQualityReportResponse> => {
    const response = await api.get("/reports/data-quality");
    return response.data;
};

// ---------------------------------------------------------------------------
// Timesheet Validation (Timesheets > Timesheet Validation, admin-only)
// ---------------------------------------------------------------------------

export interface Check1QueueItem {
    timesheet_id: number;
    employee_name: string;
    period_start: string;
    period_end: string;
    total_hours: number | null;
}

export interface Check2LineItem {
    line_id: number;
    task_name: string;
    entry_date: string;
    hours: number;
    amount: number | null;
    comments: string;
}

export interface Check2AffectedTimesheet {
    timesheet_id: number;
    employee_name: string;
    period_start: string;
    period_end: string;
    lines: Check2LineItem[];
}

export interface Check2QueueItem {
    project_id: number;
    project_name: string;
    budget: number;
    actuals: number;
    pending: number;
    remaining: number;
    affected_timesheets: Check2AffectedTimesheet[];
}

export interface ReadyToGenerateItem {
    timesheet_id: number;
    employee_name: string;
    period_start: string;
    period_end: string;
    total_hours: number | null;
}

// Refresh only ever recomputes Project Info snapshots and advances passing
// timesheets to "cleared" -- it never generates the xls. See
// generateAllClearedTimesheets below for the separate, explicit admin
// action that actually does that (Pete's requirement: even the clean/
// automatic cases wait for a deliberate admin action).
export const refreshTimesheetValidation = async (): Promise<{ status: string; cleared: number; still_pending: number }> => {
    const response = await api.post("/timesheet-validation/refresh");
    return response.data;
};

export const getTimesheetValidationQueue = async (): Promise<{
    check1_queue: Check1QueueItem[];
    check2_queue: Check2QueueItem[];
    ready_to_generate: ReadyToGenerateItem[];
}> => {
    const response = await api.get("/timesheet-validation/queue");
    return response.data;
};

// "Generate" (Timesheet Validation screen) -- the only action that ever triggers xls generation.
// excludedTimesheetIds (BACKLOG.md #17, 2026-09-10; corrected 2026-09-10 --
// the first version of this excluded individual lines, which was not what
// Pete asked for): whole timesheet IDs unchecked on the Ready to Generate
// screen -- skipped entirely (no xls, stays "cleared" so it reappears next
// time). Defaults to none excluded.
export const generateAllClearedTimesheets = async (
    excludedTimesheetIds: number[] = []
): Promise<{ status: string; generated: number; skipped: number }> => {
    const response = await api.post("/timesheet-validation/generate", { excluded_timesheet_ids: excludedTimesheetIds });
    return response.data;
};

export const approveCheck1 = async (timesheetId: number): Promise<{ status: string; advanced_to_cleared: boolean }> => {
    const response = await api.post(`/timesheet-validation/check1/${timesheetId}/approve`);
    return response.data;
};

// Rejects the ENTIRE timesheet back to the consultant with a single
// required comment -- not a line-level rejection. Every line resets to
// "pending" for a fresh full PM review once resubmitted.
export const rejectCheck1EntireTimesheet = async (timesheetId: number, reason: string): Promise<{ status: string; cc_count: number }> => {
    const response = await api.post(`/timesheet-validation/check1/${timesheetId}/reject`, { reason });
    return response.data;
};

// Per-line Approve/Reject for Check #2 (2026-08-04, v30). Approve records
// an admin override letting that line through despite the project being
// over budget; Reject behaves exactly like the Approval Queue's per-line
// rejection. Either list may be empty, but not both.
export const actionCheck2Lines = async (
    projectId: number,
    timesheetId: number,
    approvals: number[],
    rejections: { line_id: number; reason: string }[],
): Promise<{ status: string; approved_count: number; rejected_count: number; advanced_to_cleared: boolean }> => {
    const response = await api.post(
        `/timesheet-validation/check2/${projectId}/timesheet/${timesheetId}/action`,
        { approvals, rejections },
    );
    return response.data;
};

// Read-only, full cross-project breakdown of one timesheet -- backs the
// Check #1 row's "select to see details" expansion.
export interface TimesheetValidationDetailLine {
    line_id: number;
    project_name: string;
    task_name: string;
    entry_date: string;
    hours: number;
    comments: string | null;
    line_status: string;
}

export interface TimesheetValidationDetail {
    timesheet_id: number;
    employee_name: string;
    period_start: string;
    period_end: string;
    total_hours: number | null;
    status: string;
    lines: TimesheetValidationDetailLine[];
}

export const getTimesheetValidationDetail = async (timesheetId: number): Promise<TimesheetValidationDetail> => {
    const response = await api.get(`/timesheet-validation/timesheet/${timesheetId}`);
    return response.data;
};

// ---------------------------------------------------------------------------
// Timesheet Templates (My Timesheets > Timesheet Templates, backlog #7,
// 2026-08-10) -- self-scoped: a user only ever sees/manages their own.
// ---------------------------------------------------------------------------

export interface TimesheetTemplateLine {
    id?: number;
    project_id: number;
    task_id: number;
    hours: number | null;
}

export interface TimesheetTemplateSummary {
    id: number;
    name: string;
    line_count: number;
    updated_at: string | null;
}

export interface TimesheetTemplateDetail extends TimesheetTemplateSummary {
    lines: TimesheetTemplateLine[];
}

export const getTimesheetTemplates = async (): Promise<{ templates: TimesheetTemplateSummary[] }> => {
    const response = await api.get("/timesheet-templates");
    return response.data;
};

export const getTimesheetTemplateDetail = async (templateId: number): Promise<TimesheetTemplateDetail> => {
    const response = await api.get(`/timesheet-templates/${templateId}`);
    return response.data;
};

export const createTimesheetTemplate = async (
    payload: { name: string; lines: { project_id: number; task_id: number; hours: number | null }[] },
): Promise<TimesheetTemplateDetail> => {
    const response = await api.post("/timesheet-templates", payload);
    return response.data;
};

export const updateTimesheetTemplate = async (
    templateId: number,
    payload: { name: string; lines: { project_id: number; task_id: number; hours: number | null }[] },
): Promise<TimesheetTemplateDetail> => {
    const response = await api.patch(`/timesheet-templates/${templateId}`, payload);
    return response.data;
};

export const deleteTimesheetTemplate = async (templateId: number): Promise<{ status: string }> => {
    const response = await api.delete(`/timesheet-templates/${templateId}`);
    return response.data;
};

// ---------------------------------------------------------------------------
// T&M Invoice Automation (BACKLOG.md, built 2026-08-28) -- code only for
// now, staying unbuilt/undeployed until Pete reviews the whole feature.
// ---------------------------------------------------------------------------

export interface InvoicePreflightProject {
    project_id: number;
    project_name: string;
    customer_name: string | null;
    billing_type: string;
    blocked: boolean;
    blocked_reasons: string[];
}

export interface InvoiceCoverageGap {
    project_id: number;
    project_name: string;
    user_name: string;
    period_start: string;
    period_end: string;
    issue: string;
}

export interface InvoicePreflightResponse {
    bill_up_to: string;
    projects: InvoicePreflightProject[];
    coverage_gaps: InvoiceCoverageGap[];
}

export const getInvoicingPreflight = async (billUpTo: string): Promise<InvoicePreflightResponse> => {
    const response = await api.get("/invoice-automation/preflight", { params: { bill_up_to: billUpTo } });
    return response.data;
};

export interface InvoiceLineItemEntry {
    time_entry_id: string | null;
    consultant: string;
    task: string;
    date: string | null;
    hours: number;
    comments: string;
}

export interface InvoiceLineItem {
    label: string;
    hours: number;
    rate: number;
    amount: number;
    time_entry_ids: string[];
    entries: InvoiceLineItemEntry[];
    // BACKLOG.md #19 (2026-09-10): true when this line item is the pooled
    // "Amplify Subcontractor" identity rather than a real named consultant
    // -- offers the manual per-subcontractor split action on the preview
    // screen. See invoice_automation_service.py's SUBCONTRACTOR_POOL_NAME.
    is_subcontractor_pool: boolean;
}

export interface InvoicePreviewProject {
    project_id: number;
    project_name: string;
    customer_name: string | null;
    blocked: boolean;
    blocked_reasons: string[];
    over_budget: boolean;
    // BACKLOG.md #19 (2026-09-10): true whenever any of this project's
    // pooled unbilled hours belong to the shared "Amplify Subcontractor"
    // identity -- drives the project-list badge even while the row is
    // collapsed.
    has_subcontractor_pool_hours: boolean;
    // BACKLOG.md #27f (2026-09-18): true when Billing Grouping wasn't
    // explicitly set on this project and the backend had to guess a
    // default -- non-blocking warning badge, same convention as
    // has_subcontractor_pool_hours above.
    billing_grouping_is_default: boolean;
    total: number;
    line_items: InvoiceLineItem[];
}

// BACKLOG.md #19 (2026-09-10): one admin-typed row in a subcontractor
// split -- see pushInvoiceRun's subcontractorSplits param.
export interface SubcontractorSplitRow {
    name: string;
    hours: number;
}

export interface InvoicePreview {
    bill_up_to: string;
    projects: InvoicePreviewProject[];
    run_total: number;
    // BACKLOG.md #28 (2026-09-18): non-null when the configured Revenue
    // Type value isn't one of Zoho's real dropdown options -- shown
    // up-front so a stale/mistyped config value can be caught before
    // push instead of producing an opaque Zoho 400 on every project.
    revenue_type_warning: string | null;
}

export interface InvoiceRunSummary {
    id: number;
    bill_up_to_date: string;
    approved_project_ids: number[];
    created_at: string | null;
    // BACKLOG.md #27d (2026-09-18): a run is Complete once it has at
    // least one line and every line is marked_sent/voided -- computed by
    // the backend, not stored, so it can't drift out of sync.
    is_complete: boolean;
    line_count: number;
    // 2026-10-02: zero lines, or every line voided (no live Zoho invoice
    // left standing) -- see _run_can_delete in invoice_automation.py.
    // Never true for a run containing a genuinely marked_sent line.
    can_delete: boolean;
}

export interface InvoiceRunLine {
    id: number;
    project_id: number;
    project_name: string | null;
    // BACKLOG.md #27e (2026-09-18): whether this line's project has an
    // invoice_email_to on file -- true means Mark as Sent requires
    // "emailed" status first (server-enforced); false means the frontend
    // should show a one-time-per-click confirmation instead.
    project_has_invoice_email: boolean;
    zoho_invoice_id: string | null;
    invoice_number: string | null;
    amount: number | null;
    status: "pushed" | "pdf_ready" | "emailed" | "marked_sent" | "voided" | "failed";
    error_message: string | null;
    pdf_filename: string | null;
    pdf_sharepoint_url: string | null;
    pushed_at: string | null;
    pdf_fetched_at: string | null;
    emailed_at: string | null;
    marked_sent_at: string | null;
    voided_at: string | null;
}

export const listInvoiceRuns = async (): Promise<{ runs: InvoiceRunSummary[] }> => {
    const response = await api.get("/invoice-automation/runs");
    return response.data;
};

export const createInvoiceRun = async (billUpTo: string): Promise<{ run: InvoiceRunSummary; preview: InvoicePreview }> => {
    const response = await api.post("/invoice-automation/runs", { bill_up_to: billUpTo });
    return response.data;
};

// BACKLOG.md #27d (2026-09-18): only an empty run (zero lines) can
// actually be deleted -- the backend enforces this and returns a 400
// detail message otherwise.
export const deleteInvoiceRun = async (runId: number): Promise<{ deleted: boolean }> => {
    const response = await api.delete(`/invoice-automation/runs/${runId}`);
    return response.data;
};

export const refreshInvoiceRunPreview = async (runId: number): Promise<{ run: InvoiceRunSummary; preview: InvoicePreview }> => {
    const response = await api.get(`/invoice-automation/runs/${runId}/preview`);
    return response.data;
};

export const approveInvoiceRunProjects = async (runId: number, projectIds: number[]): Promise<InvoiceRunSummary> => {
    const response = await api.post(`/invoice-automation/runs/${runId}/approve`, { project_ids: projectIds });
    return response.data;
};

export const getInvoiceRun = async (runId: number): Promise<{ run: InvoiceRunSummary; lines: InvoiceRunLine[] }> => {
    const response = await api.get(`/invoice-automation/runs/${runId}`);
    return response.data;
};

export const pushInvoiceRun = async (
    runId: number, projectIds: number[], exclusions: Record<string, string[]>,
    // BACKLOG.md #19 (2026-09-10): project_id (string) -> {pooled line
    // item label -> split rows}. Entirely local state up to this call, same
    // as exclusions -- see InvoiceAutomation.tsx's subcontractorSplits.
    subcontractorSplits: Record<string, Record<string, SubcontractorSplitRow[]>> = {},
): Promise<{ run: InvoiceRunSummary; lines: InvoiceRunLine[] }> => {
    // 2026-09-10: collapsed Approve+Push into one action (see BACKLOG.md
    // #20) -- project_ids (the currently-checked set) now travels with the
    // push call itself, which persists it as the run's approved set before
    // pushing, instead of a separate prior approveInvoiceRunProjects() call.
    const response = await api.post(`/invoice-automation/runs/${runId}/push`, {
        project_ids: projectIds, exclusions, subcontractor_splits: subcontractorSplits,
    });
    return response.data;
};

export const voidInvoiceRunLine = async (runId: number, lineId: number): Promise<InvoiceRunLine> => {
    const response = await api.post(`/invoice-automation/runs/${runId}/lines/${lineId}/void`);
    return response.data;
};

// 2026-09-11: recovers a line stuck "pushed" with a PDF fetch/upload error
// (e.g. invoice_output_path wasn't configured yet at push time) -- retries
// only the PDF step, never re-pushes to Zoho (the Draft invoice already
// exists there).
export const retryInvoiceRunLinePdf = async (runId: number, lineId: number): Promise<InvoiceRunLine> => {
    const response = await api.post(`/invoice-automation/runs/${runId}/lines/${lineId}/retry-pdf`);
    return response.data;
};

export const sendInvoiceRunLineEmails = async (
    runId: number, lineIds: number[],
): Promise<{ outcomes: { line_id: number; sent: boolean; error: string | null }[] }> => {
    const response = await api.post(`/invoice-automation/runs/${runId}/lines/send-email`, { line_ids: lineIds });
    return response.data;
};

export const markInvoiceRunLinesSent = async (
    runId: number, lineIds: number[], forceLineIds: number[] = [],
): Promise<{ outcomes: {
    line_id: number; ok: boolean; error?: string;
    // 2026-10-02: set when Zoho already shows this invoice out of Draft
    // (sent another way, e.g. an older run or a manual Zoho send) --
    // frontend should confirm with the user, then resubmit this line_id
    // in forceLineIds rather than treating this as a plain failure.
    // 2026-10-02 (v79): zoho_status is only set when Zoho could actually
    // be checked. When Zoho refuses to even let the app read the
    // invoice's status (the 11102 case -- Zoho error code 57, cause
    // unresolved), zoho_status is omitted and check_error carries the
    // raw error instead -- still needs_confirmation, just a different
    // dialog (can't confirm Zoho's status, only that Pete vouches for it).
    needs_confirmation?: boolean; zoho_status?: string; check_error?: string;
}[] }> => {
    const response = await api.post(`/invoice-automation/runs/${runId}/lines/mark-sent`, {
        line_ids: lineIds, force_line_ids: forceLineIds,
    });
    return response.data;
};

export interface InvoiceSettings {
    revenue_type_field_id: string;
    revenue_type_value: string;
    email_subject_template: string;
    email_body_template: string;
    invoice_output_path: string;
}

export const getInvoiceSettings = async (): Promise<InvoiceSettings> => {
    const response = await api.get("/invoice-automation/settings");
    return response.data;
};

export const updateInvoiceSettings = async (payload: Partial<InvoiceSettings>): Promise<InvoiceSettings> => {
    const response = await api.put("/invoice-automation/settings", payload);
    return response.data;
};

export interface InvoiceProjectSetting {
    project_id: number;
    project_name: string;
    customer_name: string | null;
    billing_type: string;
    billing_grouping: string | null;
    invoice_email_to: string | null;
    invoice_email_cc: string | null;
}

export const listInvoiceProjectSettings = async (): Promise<{ projects: InvoiceProjectSetting[] }> => {
    const response = await api.get("/invoice-automation/project-settings");
    return response.data;
};

export const updateInvoiceProjectSettings = async (
    projectId: number,
    payload: Partial<{ billing_grouping: string; invoice_email_to: string; invoice_email_cc: string }>,
): Promise<{ status: string }> => {
    const response = await api.patch(`/invoice-automation/project-settings/${projectId}`, payload);
    return response.data;
};

export interface InvoiceCustomer {
    id: number;
    customer_name: string;
    abbreviation: string;
}

export const listInvoiceCustomers = async (): Promise<{ customers: InvoiceCustomer[] }> => {
    const response = await api.get("/invoice-automation/customers");
    return response.data;
};

export const upsertInvoiceCustomer = async (customerName: string, abbreviation: string): Promise<InvoiceCustomer> => {
    const response = await api.post("/invoice-automation/customers", { customer_name: customerName, abbreviation });
    return response.data;
};

export default api;
